import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import {
  AgEvent,
  CellEditingStoppedEvent,
  ColDef,
  ColumnApi,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import { BlockedEmail, BlockedEmailsQueryParams, EmailManagementApiService } from '@anduin/api';
import { FirmPortalPermission, PermissionsState } from '@anduin/auth';
import {
  AgGridCellEditableComponent,
  AgGridCellTemplateRendererComponent,
  AgGridCustomSetFilterModel,
  AgGridFactory,
  ButtonDirective,
  CellEditableParams,
  CustomSetFilterOption,
  DataSelectorDirective,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogSize,
  EMAIL_PATTERN,
  fromSync,
  getAllRowData,
  getControlValueChanges,
  IconComponent,
  ignoreNullish,
  LoadableStatus,
  LOCKED_COLUMN, MessageService,
  NULL_DATA_STRING,
  PinnedSide,
  TooltipDirective,
} from '@anduin/shared';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, finalize, firstValueFrom, forkJoin, map, Observable, of, take, tap } from 'rxjs';

const PAGE_SIZE = 50; // standard AG Grid chunk size for infinite scrolling

interface BlockedEmailRow extends BlockedEmail {
  newRowId?: number;
  updatedEmail?: string; // populated if user edits the value of the blocked email
  pendingRemoval?: boolean;
}

@Component({
  selector: 'app-manage-blocked-emails-dialog',
  templateUrl: './manage-blocked-emails-dialog.component.html',
  styleUrl: './manage-blocked-emails-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridModule,
    ReactiveFormsModule,
    ButtonDirective,
    DataSelectorDirective,
    DialogComponent,
    DialogHeaderComponent,
    DialogFooterComponent,
    IconComponent,
    TooltipDirective,
  ],
})
export class ManageBlockedEmailsDialogComponent implements AfterViewInit {
  private readonly emailManagementApi = inject(EmailManagementApiService);
  private readonly store = inject(Store);
  private readonly agGridFactory = inject(AgGridFactory);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef);

  private readonly actionsCellTemplateRef = viewChild.required<TemplateRef<unknown>>('actionsCellTemplate');

  readonly canEdit = this.store.selectSignal(PermissionsState.hasSomePermissions([FirmPortalPermission.EMAIL_MANAGEMENT_EDIT_SETTINGS]));

  readonly isLoading = signal(true);
  readonly isSubmitting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly gridApi = signal<GridApi<BlockedEmailRow> | null>(null);
  readonly columnApi = signal<ColumnApi | null>(null);
  readonly pendingRemovals = signal(new Set<string>());
  readonly editedEmails = signal(new Map<string, string>());
  readonly newRowCount = signal(0);
  readonly dirtyRowCount = computed(() => this.pendingRemovals().size + this.editedEmails().size + this.newRowCount());
  readonly emailAddressCellStateChanges$ = toObservable(this.dirtyRowCount);

  readonly searchControl = new FormControl('');

  readonly hasChanges = computed(() => this.dirtyRowCount() > 0);

  private readonly loadedCount = signal(0);
  private readonly totalCount = signal(0);
  private readonly hasMorePages = computed(() => this.loadedCount() < this.totalCount());
  private loadId = 0;

  columnDefs: ColDef<BlockedEmailRow>[] = [];

  getRowId(rowData: BlockedEmailRow): string {
    return rowData.newRowId ? `new-${rowData.newRowId}` : rowData.emailAddress;
  }

  /**
   * This table is actually server-side. We are working around the limitations set on a server-side table (like no row insertion/editing).
   * It uses `onBodyScrollEnd` to trigger loading more data.
   */
  readonly gridOptions: GridOptions<BlockedEmailRow> = {
    ...this.agGridFactory.standardGridOptions({
      getGridDataStatus: () => {
        if (this.isLoading()) return LoadableStatus.LOADING;
        if (this.errorMessage()) return LoadableStatus.ERROR;
        return LoadableStatus.LOADED;
      },
    }),
    defaultColDef: this.agGridFactory.standardColumnDef(),
    getRowId: (params) => this.getRowId(params.data),
    tooltipShowDelay: 500,
    rowClassRules: {
      'bg-color-gray-1000 color-gray-300 fst-italic': (params) => !!params.data?.pendingRemoval,
      'bg-color-primary-1000': (params) => !params.data?.pendingRemoval && !!params.data?.newRowId,
      'bg-color-yellow-900': (params) => !params.data?.pendingRemoval && !params.data?.newRowId && !!params.data?.updatedEmail,
    },
    isExternalFilterPresent: () => !!this.searchControl.value,
    postSortRows: (params) => {
      const newRows = params.nodes.filter(n => !!n.data?.newRowId).sort((a, b) => b.data.newRowId - a.data.newRowId);
      const existingRows = params.nodes.filter(n => !n.data?.newRowId);
      params.nodes.length = 0;
      params.nodes.push(...newRows, ...existingRows);
    },
    onCellEditingStopped: (event: CellEditingStoppedEvent<BlockedEmailRow>) => {
      if (event.colDef?.field !== 'emailAddress' || !event.data) return;

      // new row with empty value - remove it
      if (event.data.newRowId && !event.oldValue && !event.newValue) {
        this.gridApi()?.applyTransaction({ remove: [event.data] });
        this.newRowCount.update((n) => Math.max(0, n - 1));
        return;
      }

      // row is invalid - remove pending changes (new rows are tracked via newRowCount, not editedEmails)
      if (event['columnCellStates']?.[event.column.getColId()]?.[event.node.id]?.includes('invalid')) {
        if (!event.data.newRowId) {
          this.editedEmails.update((m) => { const next = new Map(m); next.delete(event.data.emailAddress); return next; });
        }
        return;
      }

      // value reverted back to original (existing rows only)
      if (!event.data.newRowId && event.data.updatedEmail && event.newValue === event.data.emailAddress) {
        event.api.applyTransaction({ update: [{ ...event.data, updatedEmail: undefined }] });
        this.editedEmails.update((m) => { const next = new Map(m); next.delete(event.data.emailAddress); return next; });
        return;
      }

      // value changed - only track existing rows in editedEmails; new rows are picked up from the grid directly
      if (event.newValue !== event.oldValue) {
        event.api.applyTransaction({ update: [{ ...event.data, updatedEmail: event.newValue as string }] });
        if (!event.data.newRowId) {
          this.editedEmails.update((m) => new Map(m).set(event.data.emailAddress, event.newValue as string));
        }
      }
    },
    onFilterChanged: () => this.loadBlockedEmails(),
    onSortChanged: () => this.loadBlockedEmails(),
    onBodyScrollEnd: (event) => {
      const lastRow = event.api.getLastDisplayedRow();
      const total = event.api.getDisplayedRowCount();
      if (lastRow >= total - 10 && this.hasMorePages() && !this.isLoading()) {
        this.loadNextPage();
      }
    },
    onFirstDataRendered: (event: FirstDataRenderedEvent<BlockedEmailRow>) => {
      event.columnApi.autoSizeColumns(
        event.columnApi.getAllDisplayedColumns().filter((col) => !['emailAddress', 'actions'].includes(col.getColId())),
      )
    },
    onRowDoubleClicked: (event: RowDoubleClickedEvent<BlockedEmailRow>) => {
      if (event.data?.pendingRemoval) return;
      fromSync(() => event.api.getRowNode(this.getRowId(event.data))).pipe(
        take(1),
        ignoreNullish(),
        tap((node) => {
          const instances = event.api.getCellRendererInstances({ rowNodes: [node], columns: ['emailAddress'] });
          (instances[0] as AgGridCellEditableComponent | undefined)?.setEditMode(true);
          setTimeout(() => this.gridApi().refreshCells({ rowNodes: [node], columns: ['emailAddress'] }), 1000);
        }),
      ).subscribe();
    },
  };

  ngAfterViewInit(): void {
    this.columnDefs = this.buildColumnDefs();
  }

  onGridReady(event: GridReadyEvent<BlockedEmailRow>): void {
    this.gridApi.set(event.api);
    this.columnApi.set(event.columnApi);

    // getControlValueChanges startWith(control.value) triggers the initial load synchronously on subscribe
    getControlValueChanges<string>(this.searchControl, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.loadBlockedEmails()),
      )
      .subscribe();
  }

  loadBlockedEmails(): void {
    this.loadPage(1, true);
  }

  addEmailEventCount = signal(0);
  addEmail(): void {
    this.addEmailEventCount.update((n) => n + 1);

    const newRow: BlockedEmailRow = {
      emailAddress: '',
      blockReason: 'ManuallyAdded',
      origin: 'Manual',
      createdAt: '',
      newRowId: this.addEmailEventCount(),
    };

    this.gridApi()?.applyTransaction({ add: [newRow], addIndex: 0 });
    this.newRowCount.update((n) => n + 1);
    this.gridApi()?.dispatchEvent({ type: 'rowDoubleClicked', data: newRow } as AgEvent);
  }

  removeRow(row: BlockedEmailRow): void {
    if (row.newRowId) {
      this.gridApi()?.applyTransaction({ remove: [row] });
      this.newRowCount.update((n) => Math.max(0, n - 1));
      return;
    }

    this.gridApi()?.applyTransaction({ update: [{ ...row, pendingRemoval: true }] });
    this.pendingRemovals.update((s) => new Set(s).add(row.emailAddress));
  }

  undoRemove(row: BlockedEmailRow): void {
    this.gridApi()?.applyTransaction({ update: [{ ...row, pendingRemoval: false }] });
    this.pendingRemovals.update((s) => { const next = new Set(s); next.delete(row.emailAddress); return next; });
  }

  save(): void {
    const api = this.gridApi();
    if (!api) return;

    const allRows = getAllRowData<BlockedEmailRow>(api);
    const newEmails = allRows
      .filter((r) => !!r.newRowId && !r.pendingRemoval && r.emailAddress.trim())
      .map((r) => r.emailAddress.trim());

    const toAdd = [...newEmails, ...this.editedEmails().values()];
    const toRemove = [...this.pendingRemovals(), ...this.editedEmails().keys()];

    if (!toAdd.length && !toRemove.length) return;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    forkJoin([
      toAdd.length ? this.emailManagementApi.addBlockedEmailAddresses(toAdd) : of(null),
      toRemove.length ? this.emailManagementApi.removeBlockedEmailAddresses(toRemove) : of(null),
    ])
      .pipe(
        take(1),
        tap(() => {
          this.pendingRemovals.set(new Set());
          this.editedEmails.set(new Map());
          this.newRowCount.set(0);
          this.dialogRef.close(true);
        }),
        catchError(() => {
          this.errorMessage.set('Failed to save changes. Please try again.');
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  private rehydrateRow(row: BlockedEmailRow): BlockedEmailRow {
    const pendingRemoval = this.pendingRemovals().has(row.emailAddress);
    const updatedEmail = this.editedEmails().get(row.emailAddress);
    if (!pendingRemoval && !updatedEmail) return row;
    return { ...row, pendingRemoval, updatedEmail };
  }

  private loadNextPage(): void {
    const nextPage = Math.floor(this.loadedCount() / PAGE_SIZE) + 1;
    this.loadPage(nextPage, false);
  }

  private loadPage(page: number, reset: boolean): void {
    const gridApi = this.gridApi();
    const columnApi = this.columnApi();
    if (!gridApi || !columnApi) return;

    const currentLoadId = ++this.loadId;

    const pendingNewRows = reset ? getAllRowData<BlockedEmailRow>(gridApi).filter(r => !!r.newRowId && r.updatedEmail) : [];

    if (reset) {
      this.errorMessage.set(null);
      this.loadedCount.set(0);
      this.totalCount.set(0);
      gridApi.setRowData([]);
    }

    this.isLoading.set(true);

    const filterModel = gridApi.getFilterModel() as Record<string, AgGridCustomSetFilterModel | undefined>;
    const sortedCol = columnApi.getColumnState().find((col) => col.sort);

    const query: BlockedEmailsQueryParams = {
      page,
      pageSize: PAGE_SIZE,
      sortBy: sortedCol?.colId as BlockedEmailsQueryParams['sortBy'],
      sortDirection: sortedCol?.sort || undefined,
      emailAddress: this.searchControl.value?.trim() || undefined,
      blockReason: filterModel['blockReason']?.filter?.join(',') || undefined,
      origin: filterModel['origin']?.filter?.join(',') || undefined,
    };

    this.emailManagementApi
      .getBlockedEmailAddresses(query)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (this.loadId !== currentLoadId) return;
          const rehydrated = response.items.map((row) => this.rehydrateRow(row));
          gridApi.applyTransaction({ add: rehydrated });
          this.loadedCount.update((n) => n + response.items.length);
          this.totalCount.set(response.total);
          this.isLoading.set(false);
          if (pendingNewRows.length) {
            gridApi.applyTransaction({ add: pendingNewRows, addIndex: 0 });
            // dirtyRowCount is computed from pendingRemovals + editedEmails
          }
        },
        error: () => {
          if (this.loadId !== currentLoadId) return;
          this.errorMessage.set('Failed to load suppressed emails. Please try again.');
          this.isLoading.set(false);
          if (pendingNewRows.length) {
            gridApi.applyTransaction({ add: pendingNewRows, addIndex: 0 });
            // dirtyRowCount is computed from pendingRemovals + editedEmails
          }
        },
      });
  }

  private buildColumnDefs(): ColDef<BlockedEmailRow>[] {
    return [
      {
        headerName: 'Email Address',
        field: 'emailAddress',
        // if the email is pending removal, show the original email address (cannot edit email of a pending removal)
        valueGetter: (params) => this.pendingRemovals().has(params.data?.emailAddress)
          ? params.data?.emailAddress
          : params.data?.updatedEmail ?? params.data?.emailAddress ?? '',
        filter: null,
        sortable: true,
        flex: 1,
        tooltipValueGetter: (params: ITooltipParams<BlockedEmailRow>) =>
          params.data?.updatedEmail && !params.data.newRowId ? `Original: ${params.data.emailAddress}` : null,
        cellRendererSelector: () => ({ component: AgGridCellEditableComponent }),
        cellRendererParams: {
          saveOnBlur: true,
          distribution: null,
          disableCondition: (params: ICellRendererParams<BlockedEmailRow>) => !this.canEdit() || params.data?.pendingRemoval,
          getOriginalValue: (params: ICellRendererParams<BlockedEmailRow>) =>
            (params.data?.newRowId ? params.data.updatedEmail : params.data?.emailAddress) ?? params.value,
          getStoredStateAsync: (_colId: string, nodeId: string): Observable<string> => {
            const currentNode = this.gridApi()?.getRowNode(nodeId);

            return this.emailAddressCellStateChanges$.pipe(
              map(() => {
                if (!currentNode) return '';
                const direct = !currentNode.data?.newRowId && currentNode.data?.updatedEmail ? 'direct' : '';
                const disable = !this.canEdit() || currentNode.data?.pendingRemoval ? 'disable' : '';
                return [direct, disable].filter(Boolean).join('.');
              }),
            );
          },
          ui: {
            textMode: true,
            displayValueWhenDisabled: true,
            validators: (newEmail: string) => EMAIL_PATTERN.test(newEmail) ? null : { invalidEmail: true },
            asyncValidators: async (newEmail: string, params?: CellEditableParams): Promise<ValidationErrors | null> => {
              const normalizedNew = newEmail.trim().toLowerCase();
              const api = this.gridApi();
              if (!api || normalizedNew === params?.data?.emailAddress) return null;

              const currentNodeId = params?.node?.id;
              let isDuplicateLocal = false;
              api.forEachNode((node) => {
                if (node.id !== currentNodeId) {
                  const rowEmail = (node.data?.updatedEmail ?? node.data?.emailAddress ?? '').toLowerCase();
                  if (rowEmail === normalizedNew) isDuplicateLocal = true;
                }
              });

              if (isDuplicateLocal) {
                this.messageService.error(
                  'This email address is already blocked. '
                  + `This ${params.data.newRowId ? 'addition' : 'modification'} will be ignored when saving changes.`,
                );
                return { duplicateEmail: true };
              }

              return firstValueFrom(
                this.emailManagementApi.checkBlockedEmailAddresses([newEmail]).pipe(
                  map((matches) => matches.length > 0 ? { duplicateEmail: true } : null),
                  tap((result) => {
                    if (result?.duplicateEmail) {
                      this.messageService.error(
                        'This email address is already blocked. '
                        + `This ${params.data.newRowId ? 'addition' : 'modification'} will be ignored when saving changes.`,
                      );
                    }
                  }),
                  catchError(() => of(null)),
                ),
              );
            },
            hideResetIcon: (params: CellEditableParams) => params.data?.newRowId,
          },
        },
      },
      {
        headerName: 'Reason',
        field: 'blockReason',
        sortable: true,
        filter: 'customSetFilter',
        filterParams: {
          context: {
            blockReason: {
              getSetFilterOptions: (): CustomSetFilterOption[] => [
                { value: 'HardBounce', checked: true },
                { value: 'SpamComplaint', checked: true },
                { value: 'ManuallyAdded', checked: true },
                { value: 'Unsubscribe', checked: true },
              ],
            },
          },
        },
      },
      {
        headerName: 'Origin',
        field: 'origin',
        sortable: true,
        filter: 'customSetFilter',
        filterParams: {
          context: {
            origin: {
              getSetFilterOptions: (): CustomSetFilterOption[] => [
                { value: 'Recipient', checked: true },
                { value: 'Customer', checked: true },
              ],
            },
          },
        },
      },
      {
        headerName: 'Blocked On',
        field: 'createdAt',
        sortable: true,
        valueFormatter: (p) => (p.value ? (new DatePipe('en-US').transform(p.value, 'mediumDate') ?? '') : NULL_DATA_STRING),
      },
      {
        ...LOCKED_COLUMN,
        headerName: '',
        colId: 'actions',
        minWidth: 60,
        maxWidth: 60,
        width: 60,
        pinned: PinnedSide.RIGHT,
        cellRenderer: AgGridCellTemplateRendererComponent,
        cellRendererParams: { getTemplate: () => this.actionsCellTemplateRef() },
      },
    ];
  }
  protected readonly DialogSize = DialogSize;
}
